import React, { useContext, useEffect, useState } from "react";
import {
  User,
  Phone,
  Mail,
  ShieldCheck,
  BadgeCheck,
  CalendarDays,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import Navbar from "../LandingPage/Navbar";
import Footer from "../LandingPage/Footer";
import { GlobalContext } from "../../Context/GlobalContext";
import useCustomerDetails from "../Services/Customer/useCustomerDetails";

const Profile = () => {
  const navigate = useNavigate();

  const { user, userCode } = useContext(GlobalContext);
  const { getCustomerProfileDetailsApi, customerProfileLoading } =
    useCustomerDetails();

  const [customerProfile, setCustomerProfile] = useState(null);

  const getProfileData = (apiResponse) => {
    return (
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.customer ||
      apiResponse?.result ||
      null
    );
  };

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      if (!userCode) return;

      const result = await getCustomerProfileDetailsApi({
        userId: userCode,
      });

      const profileData = getProfileData(result);

      if (profileData) {
        setCustomerProfile(profileData);
      }
    };

    fetchCustomerProfile();
  }, [userCode]);

  const profileName =
    customerProfile?.fullName ||
    customerProfile?.name ||
    customerProfile?.customerName ||
    customerProfile?.username ||
    `${customerProfile?.firstName || ""} ${
      customerProfile?.lastName || ""
    }`.trim() ||
    user?.fullName ||
    user?.name ||
    user?.username ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    "Guest User";

  const profileMobile =
    customerProfile?.mobile ||
    customerProfile?.phone ||
    customerProfile?.mobileNumber ||
    customerProfile?.customerMobile ||
    user?.mobile ||
    user?.phone ||
    user?.mobileNumber ||
    "";

  const profileEmail =
    customerProfile?.email ||
    customerProfile?.emailId ||
    user?.email ||
    "Not available";

  const profileUserId =
    customerProfile?.userId ||
    customerProfile?.userCode ||
    customerProfile?.userid ||
    userCode ||
    "N/A";

  const createdAt =
    customerProfile?.createdAt ||
    customerProfile?.created_date ||
    customerProfile?.joiningDate ||
    "Not available";

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-100 px-4 pb-12 pt-28">
        <div className="mx-auto max-w-[1050px]">
          <button
            onClick={() => navigate(-1)}
            className="mb-5 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100"
          >
            <ArrowLeft size={17} />
            Back
          </button>

          <div className="overflow-hidden rounded-[32px] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.12)]">
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 px-6 py-10 text-white md:px-10">
              <div className="absolute right-[-70px] top-[-70px] h-48 w-48 rounded-full bg-white/10" />
              <div className="absolute bottom-[-90px] left-[-70px] h-56 w-56 rounded-full bg-white/10" />

              <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-5">
                  <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-white/20 ring-4 ring-white/20">
                    <User size={52} className="text-white" />
                  </div>

                  <div>
                    {customerProfileLoading ? (
                      <>
                        <div className="mb-3 h-7 w-48 animate-pulse rounded bg-white/30" />
                        <div className="h-4 w-32 animate-pulse rounded bg-white/20" />
                      </>
                    ) : (
                      <>
                        <h1 className="text-2xl font-bold md:text-3xl">
                          {profileName}
                        </h1>

                        <p className="mt-2 flex items-center gap-2 text-white/80">
                          <BadgeCheck size={18} />
                          Customer Profile
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl bg-white/15 px-5 py-3 text-sm font-medium backdrop-blur">
                  User ID: {profileUserId}
                </div>
              </div>
            </div>

            <div className="grid gap-4 p-5 md:grid-cols-2 md:p-8">
              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <Phone size={21} />
                </div>

                <p className="text-sm text-slate-500">Mobile Number</p>

                <h3 className="mt-1 text-lg font-semibold text-slate-900">
                  {profileMobile ? `+91 ${profileMobile}` : "Not available"}
                </h3>
              </div>

              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <Mail size={21} />
                </div>

                <p className="text-sm text-slate-500">Email Address</p>

                <h3 className="mt-1 break-all text-lg font-semibold text-slate-900">
                  {profileEmail}
                </h3>
              </div>

              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <ShieldCheck size={21} />
                </div>

                <p className="text-sm text-slate-500">Account Status</p>

                <h3 className="mt-1 text-lg font-semibold text-green-600">
                  Active
                </h3>
              </div>

              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <CalendarDays size={21} />
                </div>

                <p className="text-sm text-slate-500">Member Since</p>

                <h3 className="mt-1 text-lg font-semibold text-slate-900">
                  {createdAt}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Profile;